import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import AddonCategory from '../AddonCategory';
import { Minus, Close } from '../ui/Icons';
import { formatPrice } from '../../utils/formatPrice';

/**
 * POS addon picker. Reuses the same AddonCategory component as the customer menu
 * so addon selection logic (required/multiple, default pre-selection, pricing)
 * stays identical. Returns a cart item shaped exactly like the customer flow:
 * { ...item, quantity, price: unitTotal, selectedAddons: [{category, option, price}] }.
 */
const AddonDialog = ({ open, item, addons = [], currency, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [selectedByCategory, setSelectedByCategory] = useState({});

  useEffect(() => {
    if (!open) return;
    setQuantity(1);
    const initial = {};
    addons.forEach((cat) => {
      const defaults = (cat.options || []).filter((o) => o.isDefault);
      initial[cat.id] = defaults.map((o) => ({ id: o.id, name: o.name, price: o.price, category: cat.categoryName }));
    });
    setSelectedByCategory(initial);
  }, [open, addons]);

  const allSelected = useMemo(() => Object.values(selectedByCategory).flat(), [selectedByCategory]);
  const addonsTotal = allSelected.reduce((sum, o) => sum + (Number(o.price) || 0), 0);
  const basePrice = parseFloat(item?.price) || 0;
  const unitTotal = basePrice + addonsTotal;
  const totalPrice = unitTotal * quantity;

  const missingRequired = addons
    .filter((c) => c.isRequired && (selectedByCategory[c.id] || []).length === 0)
    .map((c) => c.categoryName);
  const requiredSatisfied = missingRequired.length === 0;

  const confirm = () => {
    if (!requiredSatisfied) return;
    const selectedAddons = allSelected.map((o) => ({ category: o.category, option: o.name, price: Number(o.price) }));
    onConfirm({ ...item, quantity, price: unitTotal, selectedAddons });
  };

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <span>{item.name}</span>
        <IconButton onClick={onClose} size="small" aria-label={t('common.close', 'Close')}>
          <Close size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {addons.map((cat) => (
          <AddonCategory
            key={cat.id}
            category={cat}
            selectedOptions={selectedByCategory[cat.id] || []}
            onSelect={(opts) => setSelectedByCategory((prev) => ({ ...prev, [cat.id]: opts }))}
            currency={currency}
          />
        ))}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
          <Typography sx={{ fontWeight: 600 }}>{t('pos.menu.quantity', 'Quantity')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton size="small" onClick={() => setQuantity((q) => Math.max(1, q - 1))} aria-label={t('pos.menu.decrease', 'Decrease quantity')}>
              <Minus size={16} />
            </IconButton>
            <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 700 }}>{quantity}</Typography>
            <IconButton size="small" onClick={() => setQuantity((q) => q + 1)} aria-label={t('pos.menu.increase', 'Increase quantity')}>
              <Box component="span" sx={{ fontSize: 20, lineHeight: 1 }}>+</Box>
            </IconButton>
          </Box>
        </Box>

        {missingRequired.length > 0 && (
          <Typography sx={{ mt: 2, fontSize: 13, color: 'warning.dark' }}>
            {t('pos.menu.requiredMissing', 'Select an option for {{categories}}.', { categories: missingRequired.join(', ') })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">{t('common.cancel', 'Cancel')}</Button>
        <Button variant="contained" onClick={confirm} disabled={!requiredSatisfied}>
          {t('pos.menu.addItem', 'Add')} · {formatPrice(totalPrice, currency)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddonDialog;
